import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import { Maximize2 } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { useOverlay } from '../../../contexts/OverlayContext';
import { useScreenLogging } from '../../../hooks/useScreenLogging';
import { typography } from '../../../styles/typography';
import Card from '../../ui/card';
import { fetchSiteDataset } from './BusinessApi';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const formatDateForAPI = (date) => date.toISOString().split('T')[0];

// /** Dummy site dataset matching API response shape – used when API fails or returns empty */
// const DUMMY_SITE_DATASET = [
//   {
//     site_id: 'site-xyz-001',
//     site_name: 'Hyderabad',
//     address: '123 Restaurant Ave',
//     order_count: 42,
//     total_order_value: 1847.5,
//     hourly_throughput: {
//       '2025-04-14': {
//         '00': 0, '01': 0, '02': 0, '03': 0, '04': 0, '05': 0, '06': 0, '07': 0,
//         '08': 0, '09': 1, '10': 2, '11': 4, '12': 8, '13': 5, '14': 3, '15': 2,
//         '16': 1, '17': 2, '18': 4, '19': 3, '20': 2, '21': 0, '22': 0, '23': 0,
//       },
//       '2025-04-15': {
//         '00': 0, '01': 0, '02': 0, '03': 0, '04': 0, '05': 0, '06': 0, '07': 0,
//         '08': 0, '09': 0, '10': 3, '11': 6, '12': 9, '13': 4, '14': 2, '15': 1,
//         '16': 0, '17': 1, '18': 3, '19': 2, '20': 1, '21': 0, '22': 0, '23': 0,
//       },
//     },
//   },
//   {
//     site_id: 'site-xyz-002',
//     site_name: 'Warangal',
//     address: '456 Food Street',
//     order_count: 18,
//     total_order_value: 612.0,
//     hourly_throughput: {
//       '2025-04-14': {
//         '00': 0, '01': 0, '02': 0, '03': 0, '04': 0, '05': 0, '06': 0, '07': 0,
//         '08': 0, '09': 0, '10': 0, '11': 1, '12': 4, '13': 3, '14': 2, '15': 1,
//         '16': 0, '17': 1, '18': 2, '19': 2, '20': 2, '21': 0, '22': 0, '23': 0,
//       },
//       '2025-04-15': {
//         '00': 0, '01': 0, '02': 0, '03': 0, '04': 0, '05': 0, '06': 0, '07': 0,
//         '08': 0, '09': 0, '10': 0, '11': 0, '12': 0, '13': 0, '14': 0, '15': 0,
//         '16': 0, '17': 0, '18': 0, '19': 0, '20': 0, '21': 0, '22': 0, '23': 0,
//       },
//     },
//   },
// ];

const BusinessAnalytics = () => {
  // Log screen view
  useScreenLogging('BusinessAnalytics', {
    component: 'BusinessAnalytics',
  });
  const { openOverlay } = useOverlay();
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return formatDateForAPI(d);
  });
  const [endDate, setEndDate] = useState(() => formatDateForAPI(new Date()));
  const [isLoading, setIsLoading] = useState(false);
  const [siteData, setSiteData] = useState([]);
  const [error, setError] = useState(null);
  const [selectedSiteId, setSelectedSiteId] = useState('');

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchSiteDataset({
          startDate: new Date(startDate),
          endDate: new Date(endDate),
        });
        // Print raw API response for debugging
        console.log('[Business Analytics] Raw API response:', data);
        console.log('[Business Analytics] Is array:', Array.isArray(data), 'Length:', data?.length);
        if (Array.isArray(data) && data.length > 0) {
          console.log('[Business Analytics] First site sample:', data[0]);
          console.log('[Business Analytics] Site IDs:', data.map((s) => s.site_id || s.site_name));
        }
        const list = Array.isArray(data) ? data : [];
        console.log('[Business Analytics] Data used:', list.length, 'sites', list);
        setSiteData(list);
        if (list.length > 0 && !selectedSiteId) {
          setSelectedSiteId(list[0].site_id || '');
        }
      } catch (err) {
        console.error('Business Analytics: Error fetching site dataset', err);
        setSiteData([]);
        if (!selectedSiteId) {
          setSelectedSiteId('');
        }
        setError(null);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [startDate, endDate]);

  const summary = useMemo(() => {
    let totalOrders = 0;
    let totalValue = 0;
    siteData.forEach((site) => {
      totalOrders += Number(site.order_count) || 0;
      totalValue += Number(site.total_order_value) || 0;
    });
    const avgOrderValue =
      totalOrders > 0 ? totalValue / totalOrders : 0;
    return {
      siteCount: siteData.length,
      totalOrders,
      totalValue,
      avgOrderValue,
    };
  }, [siteData]);

  const selectedSite = useMemo(
    () => siteData.find((s) => s.site_id === selectedSiteId) || null,
    [siteData, selectedSiteId]
  );

  // Hourly throughput aggregated across all dates for selected site (for bar chart)
  const hourlyChartData = useMemo(() => {
    if (!selectedSite?.hourly_throughput) return null;
    const hourly = {};
    for (let h = 0; h < 24; h++) {
      hourly[String(h).padStart(2, '0')] = 0;
    }
    Object.values(selectedSite.hourly_throughput).forEach((dayHours) => {
      Object.entries(dayHours || {}).forEach(([hour, count]) => {
        const key = hour.length === 1 ? '0' + hour : hour;
        if (hourly[key] !== undefined) hourly[key] += Number(count) || 0;
      });
    });
    const labels = Object.keys(hourly).sort((a, b) => Number(a) - Number(b));
    const data = labels.map((h) => hourly[h]);
    return {
      labels: labels.map((h) => `${h}:00`),
      datasets: [
        {
          label: 'Orders',
          data,
          backgroundColor: 'rgba(34, 197, 94, 0.7)',
          borderColor: '#16a34a',
          borderWidth: 1,
        },
      ],
    };
  }, [selectedSite]);

  // Heatmap: date x hour for selected site
  const heatmapRows = useMemo(() => {
    if (!selectedSite?.hourly_throughput) return [];
    const dates = Object.keys(selectedSite.hourly_throughput).sort();
    return dates.map((date) => {
      const dayHours = selectedSite.hourly_throughput[date] || {};
      const row = { date, hours: [] };
      for (let h = 0; h < 24; h++) {
        const key = String(h).padStart(2, '0');
        row.hours.push(Number(dayHours[key]) || 0);
      }
      return row;
    });
  }, [selectedSite]);

  const heatmapMax = useMemo(() => {
    let max = 0;
    heatmapRows.forEach((row) => {
      row.hours.forEach((v) => {
        if (v > max) max = v;
      });
    });
    return max || 1;
  }, [heatmapRows]);

  // Throughput for all sites: site name → avg orders per active hour (2 decimals)
  const throughputPerSite = useMemo(() => {
    const result = {};
    siteData.forEach((site) => {
      const siteName = site.site_name ?? site.site_id ?? '—';
      const hourlyMap = site.hourly_throughput || {};
      const dailyThroughputs = [];
      Object.entries(hourlyMap).forEach(([, dayHours]) => {
        let totalOrders = 0;
        let activeHours = 0;
        Object.entries(dayHours || {}).forEach(([, count]) => {
          const n = Number(count);
          if (Number.isInteger(n) && n > 0) {
            totalOrders += n;
            activeHours += 1;
          }
        });
        if (activeHours > 0) {
          dailyThroughputs.push(totalOrders / activeHours);
        }
      });
      if (dailyThroughputs.length > 0) {
        const avg =
          dailyThroughputs.reduce((a, b) => a + b, 0) / dailyThroughputs.length;
        result[siteName] = Math.round(avg * 100) / 100;
      } else {
        result[siteName] = 0;
      }
    });
    return result;
  }, [siteData]);

  // Throughput for all sites – bar chart data (site name → avg orders per active hour)
  const throughputBarChartData = useMemo(() => {
    const entries = Object.entries(throughputPerSite);
    if (!entries.length) return null;
    return {
      labels: entries.map(([name]) => name),
      datasets: [
        {
          label: 'Orders per active hour',
          data: entries.map(([, value]) => value),
          backgroundColor: 'rgba(34, 197, 94, 0.7)',
          borderColor: '#16a34a',
          borderWidth: 1,
        },
      ],
    };
  }, [throughputPerSite]);

  // Sites vs Orders chart data
  const ordersBySiteChartData = useMemo(() => {
    if (!siteData.length) return null;
    return {
      labels: siteData.map((s) => s.site_name ?? s.site_id ?? '—'),
      datasets: [
        {
          label: 'Orders',
          data: siteData.map((s) => Number(s.order_count) || 0),
          backgroundColor: 'rgba(59, 130, 246, 0.7)',
          borderColor: '#2563eb',
          borderWidth: 1,
        },
      ],
    };
  }, [siteData]);

  // Sites vs Revenue (total_order_value) chart data
  const revenueBySiteChartData = useMemo(() => {
    if (!siteData.length) return null;
    return {
      labels: siteData.map((s) => s.site_name ?? s.site_id ?? '—'),
      datasets: [
        {
          label: 'Revenue (₹)',
          data: siteData.map((s) => Number(s.total_order_value) || 0),
          backgroundColor: 'rgba(168, 85, 247, 0.7)',
          borderColor: '#9333ea',
          borderWidth: 1,
        },
      ],
    };
  }, [siteData]);

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'top' },
      tooltip: {
        callbacks: {
          label: (ctx) => `Orders: ${ctx.parsed.y}`,
        },
      },
    },
    scales: {
      x: { ticks: { maxRotation: 45, maxTicksLimit: 24 } },
      y: { beginAtZero: true, ticks: { stepSize: 1 } },
    },
  };

  const revenueBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'top' },
      tooltip: {
        callbacks: {
          label: (ctx) =>
            `Revenue: ₹${Number(ctx.parsed.y).toLocaleString('en-IN')}`,
        },
      },
    },
    scales: {
      x: { ticks: { maxRotation: 45 } },
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `₹${Number(value).toLocaleString('en-IN')}`,
        },
      },
    },
  };

  return (
    <div className="w-full h-full">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className={`${typography.h2} text-black mb-1`}>
            Business Analytics
          </h1>
          <p className={`${typography.p} text-gray-600 text-sm`}>
            Site-level orders, revenue and hourly throughput
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <input
            type="date"
            value={startDate}
            max={endDate || undefined}
            onChange={(e) => {
              const val = e.target.value;
              setStartDate(val);
              if (val && endDate && val > endDate) {
                setEndDate(val);
              }
            }}
            className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
          />
          <input
            type="date"
            value={endDate}
            min={startDate || undefined}
            onChange={(e) => {
              const val = e.target.value;
              setEndDate(val);
              if (val && startDate && val < startDate) {
                setStartDate(val);
              }
            }}
            className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
          />
        </div>
      </div>

      {error && !isLoading && (
        <Card.Gradient className="p-6 mb-6 border border-red-200 bg-red-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <h3
                  className={typography.h5}
                  style={{ color: '#dc2626', marginBottom: '0.25rem' }}
                >
                  Error loading data
                </h3>
                <p className={typography.p} style={{ color: '#991b1b' }}>
                  {error}
                </p>
              </div>
            </div>
            <button
              onClick={() => setError(null)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
            >
              Dismiss
            </button>
          </div>
        </Card.Gradient>
      )}

      {isLoading && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mb-6">
            {[1, 2, 3, 4].map((i) => (
              <Card.Gradient key={i} className="p-5">
                <div className="h-4 bg-gray-200 rounded animate-pulse mb-2 w-2/3" />
                <div className="h-8 bg-gray-200 rounded animate-pulse w-3/4" />
              </Card.Gradient>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card.Gradient className="p-6">
              <div className="h-6 bg-gray-200 rounded animate-pulse mb-4 w-1/3" />
              <div className="h-[300px] bg-gray-100 rounded animate-pulse" />
            </Card.Gradient>
            <Card.Gradient className="p-6">
              <div className="h-6 bg-gray-200 rounded animate-pulse mb-4 w-1/3" />
              <div className="h-[300px] bg-gray-100 rounded animate-pulse" />
            </Card.Gradient>
          </div>
        </div>
      )}

      {!isLoading && siteData.length > 0 && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mb-6">
            <Card.Gradient
              className="p-5"
              style={{
                background: 'linear-gradient(to bottom right, #3b82f6, #2563eb)',
                boxShadow: '0px 15px 30px 0px rgba(0,0,0,0.10)',
                color: '#ffffff',
              }}
            >
              <p className="text-sm opacity-90 mb-1">Sites</p>
              <p className="text-2xl font-bold">{summary.siteCount}</p>
              <p className="text-xs opacity-75 mt-1">In date range</p>
            </Card.Gradient>
            <Card.Gradient
              className="p-5"
              style={{
                background: 'linear-gradient(to bottom right, #22c55e, #16a34a)',
                boxShadow: '0px 15px 30px 0px rgba(0,0,0,0.10)',
                color: '#ffffff',
              }}
            >
              <p className="text-sm opacity-90 mb-1">Total orders</p>
              <p className="text-2xl font-bold">{summary.totalOrders}</p>
              <p className="text-xs opacity-75 mt-1">Across all sites</p>
            </Card.Gradient>
            <Card.Gradient
              className="p-5"
              style={{
                background: 'linear-gradient(to bottom right, #a855f7, #9333ea)',
                boxShadow: '0px 15px 30px 0px rgba(0,0,0,0.10)',
                color: '#ffffff',
              }}
            >
              <p className="text-sm opacity-90 mb-1">Total order value</p>
              <p className="text-2xl font-bold">
                ₹{summary.totalValue.toLocaleString('en-IN')}
              </p>
              <p className="text-xs opacity-75 mt-1">Revenue</p>
            </Card.Gradient>
            <Card.Gradient
              className="p-5"
              style={{
                background: 'linear-gradient(to bottom right, #f97316, #ea580c)',
                boxShadow: '0px 15px 30px 0px rgba(0,0,0,0.10)',
                color: '#ffffff',
              }}
            >
              <p className="text-sm opacity-90 mb-1">Avg order value</p>
              <p className="text-2xl font-bold">
                ₹{summary.avgOrderValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </p>
              <p className="text-xs opacity-75 mt-1">Per order</p>
            </Card.Gradient>
          </div>

          <Card.Gradient className="p-6 mb-6 overflow-hidden">
            <h3
              className={typography.h5}
              style={{ color: '#000', marginBottom: '1rem' }}
            >
              Sites overview
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className={typography.sh2 + ' py-3 px-3 text-gray-700'}>
                      Site
                    </th>
                    <th className={typography.sh2 + ' py-3 px-3 text-gray-700'}>
                      Orders
                    </th>
                    <th className={typography.sh2 + ' py-3 px-3 text-gray-700'}>
                      Order value
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {siteData.map((site) => (
                    <tr
                      key={site.site_id}
                      className="border-b border-gray-100 hover:bg-gray-50/50"
                    >
                      <td className="py-2 px-3 text-sm font-medium text-gray-800">
                        {site.site_name ?? site.site_id ?? '—'}
                      </td>
                      <td className="py-2 px-3 text-sm">
                        {(site.order_count ?? 0).toLocaleString('en-IN')}
                      </td>
                      <td className="py-2 px-3 text-sm">
                        ₹{(Number(site.total_order_value) || 0).toLocaleString('en-IN')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card.Gradient>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {hourlyChartData && (
              <Card.Gradient className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <h3
                      className={typography.h5}
                      style={{ color: '#000' }}
                    >
                      Hourly throughput
                    </h3>
                    <button
                      type="button"
                      onClick={() =>
                        openOverlay('businessAnalyticsChart', {
                          title: 'Hourly throughput',
                          content: (
                            <div style={{ height: '70vh', width: '100%' }}>
                              <Bar data={hourlyChartData} options={barOptions} />
                            </div>
                          ),
                        })
                      }
                      className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                      aria-label="Expand chart"
                    >
                      <Maximize2 className="w-4 h-4" />
                    </button>
                  </div>
                  <select
                    value={selectedSiteId}
                    onChange={(e) => setSelectedSiteId(e.target.value)}
                    className="h-9 px-3 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
                  >
                    {siteData.map((site) => (
                      <option key={site.site_id} value={site.site_id}>
                        {site.site_name ?? site.site_id}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ height: '300px' }}>
                  <Bar data={hourlyChartData} options={barOptions} />
                </div>
              </Card.Gradient>
            )}

            {heatmapRows.length > 0 && selectedSiteId && (
              <Card.Gradient className="p-6 overflow-hidden">
                <div className="flex items-center justify-between mb-2">
                  <h3
                    className={typography.h5}
                    style={{ color: '#000' }}
                  >
                    Throughput by date & hour
                  </h3>
                  <button
                    type="button"
                    onClick={() =>
                      openOverlay('businessAnalyticsChart', {
                        title: 'Throughput by date & hour',
                        content: (
                          <div className="overflow-x-auto">
                            <p className={`${typography.p} text-gray-600 text-sm mb-3`}>
                              {selectedSite?.site_name ?? selectedSiteId}
                            </p>
                            <div className="inline-block min-w-full">
                              <div className="flex gap-0.5">
                                <div className="flex flex-col gap-0.5 pr-2">
                                  <div className="h-6 font-semibold text-xs text-gray-600" />
                                  {heatmapRows.map((row) => (
                                    <div
                                      key={row.date}
                                      className="h-6 flex items-center text-xs text-gray-700 font-medium"
                                    >
                                      {row.date}
                                    </div>
                                  ))}
                                </div>
                                <div className="flex-1 flex flex-col gap-0.5">
                                  <div className="flex gap-0.5 mb-1">
                                    {Array.from({ length: 24 }, (_, i) => (
                                      <div
                                        key={i}
                                        className="w-5 text-center text-[10px] text-gray-500 font-medium"
                                        title={`${String(i).padStart(2, '0')}:00`}
                                      >
                                        {i % 3 === 0 ? i : ''}
                                      </div>
                                    ))}
                                  </div>
                                  {heatmapRows.map((row) => (
                                    <div key={row.date} className="flex gap-0.5">
                                      {row.hours.map((val, h) => {
                                        const intensity = heatmapMax ? (val / heatmapMax) * 100 : 0;
                                        return (
                                          <div
                                            key={h}
                                            className="w-5 h-6 rounded border border-gray-200 flex items-center justify-center text-[10px] font-medium"
                                            style={{
                                              backgroundColor: `rgba(34, 197, 94, ${intensity / 100})`,
                                              color: intensity > 50 ? 'white' : 'black',
                                            }}
                                            title={`${row.date} ${String(h).padStart(2, '0')}:00 — ${val} orders`}
                                          >
                                            {val > 0 ? val : ''}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        ),
                      })
                    }
                    className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                    aria-label="Expand chart"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </button>
                </div>
                <p className={`${typography.p} text-gray-600 text-sm mb-3`}>
                  {selectedSite?.site_name ?? selectedSiteId}
                </p>
                <div className="overflow-x-auto">
                  <div className="inline-block min-w-full">
                    <div className="flex gap-0.5">
                      <div className="flex flex-col gap-0.5 pr-2">
                        <div className="h-6 font-semibold text-xs text-gray-600" />
                        {heatmapRows.map((row) => (
                          <div
                            key={row.date}
                            className="h-6 flex items-center text-xs text-gray-700 font-medium"
                          >
                            {row.date}
                          </div>
                        ))}
                      </div>
                      <div className="flex-1 flex flex-col gap-0.5">
                        <div className="flex gap-0.5 mb-1">
                          {Array.from({ length: 24 }, (_, i) => (
                            <div
                              key={i}
                              className="w-5 text-center text-[10px] text-gray-500 font-medium"
                              title={`${String(i).padStart(2, '0')}:00`}
                            >
                              {i % 3 === 0 ? i : ''}
                            </div>
                          ))}
                        </div>
                        {heatmapRows.map((row) => (
                          <div
                            key={row.date}
                            className="flex gap-0.5"
                          >
                            {row.hours.map((val, h) => {
                              const intensity = heatmapMax ? (val / heatmapMax) * 100 : 0;
                              return (
                                <div
                                  key={h}
                                  className="w-5 h-6 rounded border border-gray-200 flex items-center justify-center text-[10px] font-medium"
                                  style={{
                                    backgroundColor: `rgba(34, 197, 94, ${intensity / 100})`,
                                    color: intensity > 50 ? 'white' : 'black',
                                  }}
                                  title={`${row.date} ${String(h).padStart(2, '0')}:00 — ${val} orders`}
                                >
                                  {val > 0 ? val : ''}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </Card.Gradient>
            )}
          </div>

          {/* Throughput for all sites – full width bar chart */}
          {throughputBarChartData && (
            <Card.Gradient className="p-6 mb-6 w-full overflow-hidden">
              <div className="flex items-center justify-between mb-2">
                <h3
                  className={typography.h5}
                  style={{ color: '#000' }}
                >
                  Throughput for all sites
                </h3>
                <button
                  type="button"
                  onClick={() =>
                    openOverlay('businessAnalyticsChart', {
                      title: 'Throughput for all sites',
                      content: (
                        <div style={{ height: '70vh', width: '100%' }}>
                          <Bar
                            data={throughputBarChartData}
                            options={{
                              ...barOptions,
                              plugins: {
                                ...barOptions.plugins,
                                tooltip: {
                                  callbacks: {
                                    label: (ctx) =>
                                      `${ctx.parsed.y.toFixed(2)} orders/active hr`,
                                  },
                                },
                              },
                              scales: {
                                ...barOptions.scales,
                                y: {
                                  beginAtZero: true,
                                  ticks: {
                                    callback: (value) => value.toFixed(1),
                                  },
                                },
                              },
                            }}
                          />
                        </div>
                      ),
                    })
                  }
                  className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                  aria-label="Expand chart"
                >
                  <Maximize2 className="w-4 h-4" />
                </button>
              </div>
              <p className={`${typography.p} text-gray-600 text-sm mb-4`}>
                Average orders per active hour
              </p>
              <div style={{ height: '300px' }}>
                <Bar
                  data={throughputBarChartData}
                  options={{
                    ...barOptions,
                    plugins: {
                      ...barOptions.plugins,
                      tooltip: {
                        callbacks: {
                          label: (ctx) =>
                            `${ctx.parsed.y.toFixed(2)} orders/active hr`,
                        },
                      },
                    },
                    scales: {
                      ...barOptions.scales,
                      y: {
                        beginAtZero: true,
                        ticks: {
                          callback: (value) => value.toFixed(1),
                        },
                      },
                    },
                  }}
                />
              </div>
            </Card.Gradient>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {ordersBySiteChartData && (
              <Card.Gradient className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3
                    className={typography.h5}
                    style={{ color: '#000' }}
                  >
                    Overall Orders
                  </h3>
                  <button
                    type="button"
                    onClick={() =>
                      openOverlay('businessAnalyticsChart', {
                        title: 'Overall Orders',
                        content: (
                          <div style={{ height: '70vh', width: '100%' }}>
                            <Bar data={ordersBySiteChartData} options={barOptions} />
                          </div>
                        ),
                      })
                    }
                    className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                    aria-label="Expand chart"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </button>
                </div>
                <div style={{ height: '300px' }}>
                  <Bar data={ordersBySiteChartData} options={barOptions} />
                </div>
              </Card.Gradient>
            )}
            {revenueBySiteChartData && (
              <Card.Gradient className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3
                    className={typography.h5}
                    style={{ color: '#000' }}
                  >
                    Overall Revenue
                  </h3>
                  <button
                    type="button"
                    onClick={() =>
                      openOverlay('businessAnalyticsChart', {
                        title: 'Overall Revenue',
                        content: (
                          <div style={{ height: '70vh', width: '100%' }}>
                            <Bar
                              data={revenueBySiteChartData}
                              options={revenueBarOptions}
                            />
                          </div>
                        ),
                      })
                    }
                    className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                    aria-label="Expand chart"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </button>
                </div>
                <div style={{ height: '300px' }}>
                  <Bar
                    data={revenueBySiteChartData}
                    options={revenueBarOptions}
                  />
                </div>
              </Card.Gradient>
            )}
          </div>
        </>
      )}

      {!isLoading && siteData.length === 0 && !error && (
        <Card.Gradient className="p-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-10 h-10 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <h3
              className={typography.h4}
              style={{ color: '#000', marginBottom: '0.5rem' }}
            >
              No site data in this range
            </h3>
            <p className={typography.p} style={{ color: '#666' }}>
              Try a different date range
            </p>
          </div>
        </Card.Gradient>
      )}
    </div>
  );
};

export default BusinessAnalytics;
